import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  V2Tabs,
  LoadingState,
  StatePanel,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { businessDate, compareBusinessDates, systemClock } from '@sakhtar/calendar';
import { AgingBucketsChart, formatChartAmount } from '../../lib/charts/index.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useTenantSettings } from '../../lib/tenantSettings.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type {
  AccountListResponse,
  AgingResponse,
  OpenItem,
  OpenItemListResponse,
  OpenItemStatus,
} from '../../lib/types/finance.js';
import {
  agingBucketLabel,
  amountColumnHeader,
  amountUnitLabel,
  formatRialFa,
  openItemHref,
  OPEN_ITEM_STATUS_TONE,
  sourceTypeLabel,
  subledgerSourceHref,
} from '../../lib/finance-display.js';
import { PartyLabel } from './PartyLabel.js';
import { OpenItemCreateCard } from './OpenItemCreateCard.js';
import { OpenItemsAgingCard } from './OpenItemsAgingCard.js';
import { OpenItemsPartyBalances } from './OpenItemsPartyBalances.js';
import { OpenItemsPaymentSchedule } from './OpenItemsPaymentSchedule.js';
import { OpenItemsTreasuryTab } from './OpenItemsTreasuryTab.js';
import { OpenItemsSalesReturnsTab } from './OpenItemsSalesReturnsTab.js';
import { SalesReturnCreateModal } from './SalesReturnCreateModal.js';
import { aggregateByParty, sumRial } from './OpenItemAggregates.js';

type OpenItemsTab = 'balances' | 'items' | 'txns' | 'returns';

const STATUS_FILTERS: ReadonlyArray<{ value: '' | OpenItemStatus; label: string }> = [
  { value: '', label: 'همه' },
  { value: 'open', label: 'باز' },
  { value: 'partial', label: 'تسویه ناقص' },
  { value: 'settled', label: 'تسویه‌شده' },
  { value: 'draft', label: 'پیش‌نویس' },
  { value: 'reversed', label: 'برگشت‌خورده' },
];

interface ItemRow extends OpenItem {
  readonly id: string;
}

function isOverdue(item: OpenItem, today: string): boolean {
  if (item.status !== 'open' && item.status !== 'partial') return false;
  try {
    return compareBusinessDates(businessDate(item.dueDate.slice(0, 10)), businessDate(today)) < 0;
  } catch {
    return false;
  }
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — /t/kimia/finance, finAr / finAp
 *   (دریافتنی‌ها / پرداختنی‌ها), with the Finance.dc.html sub-tabs
 *   (AR: مانده طرف‌حساب‌ها · اقلام باز · دریافت‌ها; AP: مانده تأمین‌کنندگان ·
 *   اقلام پرداختنی · پرداخت‌ها).
 * archetype: ENTITY_LIST
 * canonical pattern: aging card (severity bar + bucket tiles) → per-party
 *   balances grid with totals row → open-items grid with totals row; AP adds
 *   the weekly payment schedule + supplier share bars.
 * desktop reference: finAr / finAp frames in docs/design-system/reference/Sakhtar Cloud ERP v2.dc.html
 * mobile reference: v2 mobile shell untouched; cards/grids wrap (flex-wrap, sk-table-scroll)
 * light/dark reference: tokens only (--sk-*), no literal colours
 * state reference: loading · empty · permission_denied (403) · network_error · ready
 * status: PARTIAL_REAL — list/detail/aging/create are real
 *   (apps/api/src/finance/{ar,ap}.controller.ts). IMPORTANT data facts, verified:
 *   `GET .../open-items` and `GET .../aging` are NOT paged
 *   (packages/modules/accounting/src/subledger/repository.ts:318-335
 *   `listOpenItems` has no limit/offset), so per-party sums and totals computed
 *   here from the aging items are true totals, not a page subset. Aging items
 *   are open/partial only (aging.ts:46) and carry `daysPastDue`.
 *   Omitted because no backend exists (grep over apps/api/src/finance and
 *   packages/modules/accounting finds none): «چک‌های دریافتنی» (cheque
 *   register / holder status), «تطبیق سه‌طرفه» (PO · goods receipt · invoice
 *   match) and its «منتظر رسید انبار / اختلاف مبلغ» statuses, reminder/follow-up
 *   row actions (یادآوری/پیگیری), and a settlements (تسویه‌ها) list — settlement
 *   allocations are only in the treasury transaction detail. The payment
 *   schedule is derived from real due dates, not a payment-run table.
 *
 * Create (`POST .../finance/{ar|ap}/open-items`) is real — a manual open
 * item with its own canonical draft journal, now in OpenItemCreateCard. The
 * handoff-created rows (sourceType `sales_handoff` / `procurement_handoff`)
 * still only come from CRM/procurement automatically.
 */
export function OpenItemsPage({ kind }: { readonly kind: 'ar' | 'ap' }) {
  const { slug } = useParams<{ slug: string }>();
  const { amountDisplayUnit } = useTenantSettings();
  const unit = amountDisplayUnit;
  const unitLabel = amountUnitLabel(unit);
  const base = slug ? tenantBasePath(slug) : '';
  const today = systemClock.today();
  const [tab, setTab] = useState<OpenItemsTab>('balances');
  const [status, setStatus] = useState<'' | OpenItemStatus>('');
  const [partyFilter, setPartyFilter] = useState('');
  const [asOfDate, setAsOfDate] = useState<string>(today);
  const [refreshKey, setRefreshKey] = useState(0);
  // Quick-add («دریافتنی جدید» / «پرداختنی جدید») links here with ?new=1 so
  // the create form opens straight away instead of landing on the balances.
  const [searchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(() => searchParams.get('new') === '1');
  const [showSalesReturnModal, setShowSalesReturnModal] = useState(false);
  const effectiveAsOf = asOfDate || today;
  const isAr = kind === 'ar';
  const title = isAr ? 'دریافتنی‌ها' : 'پرداختنی‌ها';
  const emptyTitle = isAr ? 'دریافتنی ثبت نشده' : 'پرداختنی ثبت نشده';
  const partyNoun = isAr ? 'مشتری' : 'تأمین‌کننده';

  const listQuery = [
    status ? `status=${status}` : '',
    partyFilter ? `partyId=${encodeURIComponent(partyFilter)}` : '',
  ]
    .filter(Boolean)
    .join('&');
  const items = useApiGet<OpenItemListResponse>(
    slug && tab === 'items'
      ? `${base}/api/v1/finance/${kind}/open-items${listQuery ? `?${listQuery}` : ''}`
      : null,
    [slug, kind, tab, status, partyFilter, refreshKey],
  );
  const aging = useApiGet<AgingResponse>(
    slug ? `${base}/api/v1/finance/${kind}/aging?asOfDate=${effectiveAsOf}` : null,
    [slug, kind, effectiveAsOf, refreshKey],
  );
  const glAccounts = useApiGet<AccountListResponse>(
    slug ? `${base}/api/v1/finance/accounts` : null,
    [slug],
  );

  const accountCodeById = useMemo(
    () =>
      new Map(
        glAccounts.status === 'ready'
          ? glAccounts.data.items.map((a) => [a.accountId, a.code] as const)
          : [],
      ),
    [glAccounts],
  );
  const partyRows = useMemo(
    () => (aging.status === 'ready' ? aggregateByParty(aging.data.items) : []),
    [aging],
  );
  const rows: ItemRow[] = useMemo(() => {
    if (items.status !== 'ready') return [];
    return items.data.items.map((item) => ({ ...item, id: item.openItemId }));
  }, [items]);

  if (!slug) return null;

  const tabItems = [
    { id: 'balances', label: isAr ? 'مانده طرف‌حساب‌ها' : 'مانده تأمین‌کنندگان' },
    { id: 'items', label: isAr ? 'اقلام باز' : 'اقلام پرداختنی' },
    { id: 'txns', label: isAr ? 'دریافت‌ها' : 'پرداخت‌ها' },
    ...(isAr ? [{ id: 'returns', label: 'برگشت از فروش (بستانکار)' }] : []),
  ];

  const itemColumns: readonly V2GridColumn<ItemRow>[] = [
    {
      key: 'reference',
      header: isAr ? 'فاکتور' : 'فاکتور خرید',
      render: (row) => {
        const href = subledgerSourceHref(kind, row.sourceType, row.sourceReference, base);
        const label = row.sourceReference || '—';
        return href ? (
          <Link className="ltr sk-num" to={href} style={{ fontFamily: 'var(--sk-font-mono)' }}>
            {label}
          </Link>
        ) : (
          <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'party',
      header: isAr ? 'طرف‌حساب' : 'تأمین‌کننده',
      render: (row) => (
        <PartyLabel
          slug={slug}
          partyId={row.partyId}
          href={`${base}/master-data/parties/${row.partyId}`}
        />
      ),
    },
    {
      key: 'source',
      header: 'منبع',
      render: (row) => <span>{sourceTypeLabel(row.sourceType)}</span>,
    },
    {
      key: 'due',
      header: 'سررسید',
      render: (row) => (
        <span
          className="sk-num"
          style={isOverdue(row, today) ? { color: 'var(--sk-danger)', fontWeight: 600 } : undefined}
        >
          {displayJalaliDate(row.dueDate)}
        </span>
      ),
    },
    {
      key: 'original',
      header: amountColumnHeader('مبلغ', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.originalAmountRial, unit)}</span>,
      footer: (
        <span className="sk-num">
          {formatRialFa(sumRial(rows.map((r) => r.originalAmountRial)).toString(), unit)}
        </span>
      ),
    },
    {
      key: 'open',
      header: amountColumnHeader('مانده', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.openBalanceRial, unit)}</span>,
      footer: (
        <span className="sk-num">
          {formatRialFa(sumRial(rows.map((r) => r.openBalanceRial)).toString(), unit)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={OPEN_ITEM_STATUS_TONE[row.status] ?? 'neutral'}>{row.statusLabel}</V2Badge>
      ),
    },
    {
      key: 'openItem',
      header: '',
      hideable: false,
      render: (row) => (
        <Link to={openItemHref(base, kind, row.openItemId)} style={{ fontSize: 13 }}>
          جزئیات
        </Link>
      ),
    },
  ];

  const agingHeaderActions = (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      سن بدهی تا تاریخ
      <JalaliDateInput value={asOfDate} onChange={setAsOfDate} />
    </label>
  );

  function openItemsFor(partyId: string) {
    setPartyFilter(partyId);
    setStatus('');
    setTab('items');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
        {title} — ثبت هر فاکتور یک سند حسابداری پیش‌نویس می‌سازد؛ پس از قطعی‌شدن سند، قلم باز قابل
        تسویه می‌شود و مانده‌ها به‌روز می‌شوند.
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <V2Tabs items={tabItems} activeId={tab} onChange={(id) => setTab(id as OpenItemsTab)} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isAr && (
            <V2Button
              variant="secondary"
              size="sm"
              icon="plus"
              onClick={() => setShowSalesReturnModal(true)}
            >
              ثبت برگشت از فروش
            </V2Button>
          )}
          <V2Button
            variant="primary"
            size="sm"
            icon="plus"
            onClick={() => {
              setTab('items');
              setShowCreate(true);
            }}
          >
            {isAr ? 'ثبت دریافتنی جدید' : 'ثبت پرداختنی جدید'}
          </V2Button>
        </div>
      </div>

      {tab === 'balances' ? (
        <>
          {aging.status === 'loading' ? <LoadingState /> : null}
          {aging.status === 'error' ? (
            <StatePanel
              variant={aging.error.status === 403 ? 'permission_denied' : 'network_error'}
              title={
                aging.error.status === 403
                  ? `دسترسی به ${title} ندارید`
                  : 'مانده و سن بدهی قابل نمایش نیست'
              }
            />
          ) : null}
          {aging.status === 'ready' ? (
            <>
              <OpenItemsAgingCard
                kind={kind}
                buckets={aging.data.buckets}
                asOfDate={effectiveAsOf}
                unit={unit}
                headerActions={agingHeaderActions}
              >
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                    نمودار مقایسهٔ بازه‌ها · جمع{' '}
                    {(() => {
                      const total = sumRial(aging.data.buckets.map((b) => b.openBalanceRial));
                      return total <= BigInt(Number.MAX_SAFE_INTEGER)
                        ? formatChartAmount(total, {
                            unit: amountDisplayUnit,
                            includeSuffix: true,
                          })
                        : `${formatRialFa(total.toString(), unit)} ${unitLabel}`;
                    })()}
                  </summary>
                  <AgingBucketsChart
                    ariaLabel={`سن بدهی ${title} تا ${displayJalaliDate(effectiveAsOf)}`}
                    amountUnit={amountDisplayUnit}
                    buckets={aging.data.buckets.map((b) => ({
                      bucket: b.bucket,
                      label: agingBucketLabel(b.bucket),
                      openBalanceRial: Number(b.openBalanceRial),
                    }))}
                  />
                </details>
              </OpenItemsAgingCard>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: '1.5 1 520px', minWidth: 0 }}>
                  <OpenItemsPartyBalances
                    kind={kind}
                    slug={slug}
                    base={base}
                    rows={partyRows}
                    unit={unit}
                    accountCodeById={accountCodeById}
                    onShowItems={openItemsFor}
                  />
                </div>
                {!isAr ? (
                  <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <OpenItemsPaymentSchedule
                      base={base}
                      slug={slug}
                      items={aging.data.items}
                      partyRows={partyRows}
                      unit={unit}
                      asOfDate={effectiveAsOf}
                    />
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {tab === 'items' ? (
        <>
          {showCreate ? (
            <OpenItemCreateCard
              kind={kind}
              slug={slug}
              base={base}
              unit={unit}
              unitLabel={unitLabel}
              onCreated={() => {
                setShowCreate(false);
                setRefreshKey((k) => k + 1);
              }}
            />
          ) : null}
          {partyFilter ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ color: 'var(--sk-fg-muted)' }}>{partyNoun}:</span>
              <PartyLabel slug={slug} partyId={partyFilter} />
              <V2Button variant="ghost" size="sm" onClick={() => setPartyFilter('')}>
                نمایش همهٔ {partyNoun}‌ها
              </V2Button>
            </div>
          ) : null}
          {items.status === 'loading' ? <LoadingState /> : null}
          {items.status === 'error' ? (
            <StatePanel
              variant={items.error.status === 403 ? 'permission_denied' : 'network_error'}
              title={items.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
            />
          ) : null}
          {items.status === 'ready' ? (
            <V2DataGrid
              icon="invoice"
              title={isAr ? 'اقلام باز دریافتنی' : 'فاکتورهای خرید و اقلام پرداختنی'}
              subtitle={`${rows.length.toLocaleString('fa-IR')} مورد`}
              filters={[
                {
                  id: 'status',
                  label: 'وضعیت',
                  value: status,
                  options: STATUS_FILTERS.map((o) => ({ value: o.value, label: o.label })),
                  onChange: (value) => setStatus(value as '' | OpenItemStatus),
                },
              ]}
              columns={itemColumns}
              rows={rows}
              rowKey={(r) => r.id}
              rowTone={(r) => (isOverdue(r, today) ? 'danger' : undefined)}
              footerLabel={
                <>
                  جمع · <span className="sk-num">{rows.length.toLocaleString('fa-IR')}</span>{' '}
                  {isAr ? 'قلم' : 'فاکتور'}
                </>
              }
              zebra
              emptyState={<V2EmptyState icon="info" title={emptyTitle} />}
            />
          ) : null}
        </>
      ) : null}

      {tab === 'txns' ? (
        <OpenItemsTreasuryTab kind={kind} slug={slug} base={base} unit={unit} />
      ) : null}

      {tab === 'returns' && isAr ? (
        <OpenItemsSalesReturnsTab slug={slug} base={base} unit={unit} unitLabel={unitLabel} />
      ) : null}

      {isAr && (
        <SalesReturnCreateModal
          open={showSalesReturnModal}
          onClose={() => setShowSalesReturnModal(false)}
          onSuccess={() => {
            setRefreshKey((k) => k + 1);
            setTab('returns');
          }}
          slug={slug}
          base={base}
          unit={unit}
          unitLabel={unitLabel}
        />
      )}
    </div>
  );
}
